import React, { useEffect } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";

import useRegion from "../../../../hooks/super/useRegion";
import useWAFEdge from "../../../../hooks/super/nodes/useWAFEdge";

import { TOKENS, FormDrawer } from "../../../../components/pages/_new/super/design";

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.fg2,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: TOKENS.bgSurface,
    fontSize: 14,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.border },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.primary },
};

function RegionModal({ open, handleClose, region, loading }) {
  const { createRegion, updateRegion } = useRegion();
  const { allWafEdges, getAllWAFs } = useWAFEdge();

  useEffect(() => {
    if (open) {
      getAllWAFs();
    }
  }, [open, getAllWAFs]);

  const initialValues = {
    name: region?.name ? region.name : "",
    host_name: region?.host_name ? region.host_name : "",
    edge_ip: region?.edge_ip ? region.edge_ip : "",
    res_code: region?.res_code ? region.res_code : 200,
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(255).required("Name is required"),
        host_name: Yup.string().max(255).required("Host name is required"),
        edge_ip: Yup.string().max(255).required("Edge private IP address is required"),
        res_code: Yup.number().integer().min(100).max(599).required(),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          if (region) {
            await updateRegion(region.id, values);
          } else {
            await createRegion(values);
          }
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue, resetForm, dirty }) => (
        <FormDrawer
          open={open}
          onClose={() => {
            handleClose();
            resetForm();
          }}
          title={region ? "Edit Region" : "Add Region"}
          submitLabel={region ? "Save changes" : "Create"}
          loading={isSubmitting || loading}
          canSubmit={!isSubmitting}
          onSubmit={handleSubmit}
          isDirty={dirty}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {errors.submit && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: "10px" }}>
                    {errors.submit}
                  </Alert>
                )}

                <Box>
                  <Typography sx={fieldLabelSx}>Region Name *</Typography>
                  <TextField
                    name="name"
                    value={values.name}
                    fullWidth
                    size="small"
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Host Name *</Typography>
                  <TextField
                    name="host_name"
                    value={values.host_name}
                    fullWidth
                    size="small"
                    error={Boolean(touched.host_name && errors.host_name)}
                    helperText={touched.host_name && errors.host_name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Edge Private IP Address *</Typography>
                  <Autocomplete
                    id="edge_ip"
                    options={allWafEdges || []}
                    freeSolo
                    autoHighlight
                    fullWidth
                    getOptionLabel={(option) => (typeof option === "string" ? option : option?.ip || "")}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.ip} ({option.name})
                      </li>
                    )}
                    onChange={(e, value) => {
                      if (value && typeof value !== "string") {
                        setFieldValue("edge_ip", value.ip);
                      } else if (typeof value === "string") {
                        setFieldValue("edge_ip", value);
                      } else {
                        setFieldValue("edge_ip", initialValues.edge_ip);
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    inputValue={values.edge_ip}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="edge_ip"
                        size="small"
                        fullWidth
                        error={Boolean(touched.edge_ip && errors.edge_ip)}
                        helperText={touched.edge_ip && errors.edge_ip}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Select a WAF Edge"
                        sx={inputSx}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Response Code *</Typography>
                  <TextField
                    name="res_code"
                    value={values.res_code}
                    fullWidth
                    size="small"
                    type="number"
                    error={Boolean(touched.res_code && errors.res_code)}
                    helperText={touched.res_code && errors.res_code}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>
              </Stack>
            </form>
          )}
        </FormDrawer>
      )}
    </Formik>
  );
}

export default RegionModal;
